/**
 * Reusable on-screen modal — same overlay + card shell as rent confirmation (`modals.module.css`).
 */
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import modalStyles from "@/features/application/styles/modals.module.css";
import claimsModalStyles from "@/components/ui/claimsModals.module.css";

const MAX_WIDTH_CLASS: Record<string, string> = {
  "max-w-md": modalStyles.modalContentCompact,
  "max-w-lg": modalStyles.modalContent32,
  "max-w-xl": modalStyles.modalContent36,
  "max-w-2xl": modalStyles.modalContent42,
  "max-w-3xl": modalStyles.modalContent48,
  /** Parity with Angular `user-roles-edit` `.popupwidth` (800px). */
  "max-w-800": modalStyles.modalContent800,
  /** Angular email-parser-detail `.form-width` (900px). */
  "max-w-900": modalStyles.modalContent900,
  /** Wide configure-payment dialogs (~1100px). */
  "max-w-1100": claimsModalStyles.modalContent1100,
  /** Compact form dialogs (Advance Property Search). */
  "max-w-640": modalStyles.modalContent640,
};

interface OnScreenModalProps {
  isOpen: boolean;
  title: string;
  children: ReactNode;
  onClose: () => void;
  hideClose?: boolean;
  /** Tailwind-style max width token — default `max-w-lg` */
  maxWidth?: string;
  /** Optional hooks for E2E (e.g. transfer / invitee modals). */
  overlayDataTestId?: string;
  panelDataTestId?: string;
  closeButtonDataTestId?: string;
  titleDataTestId?: string;
  /** Extra classes for the scrollable body (e.g. max-height for tall forms). */
  bodyClassName?: string;
  /** Extra classes for the backdrop overlay (e.g. higher z-index when nested). */
  overlayClassName?: string;
  /** Extra classes for the modal card/panel. */
  panelClassName?: string;
  /** Extra classes for the header chrome. */
  headerClassName?: string;
  /** Extra classes for the footer chrome. */
  footerClassName?: string;
  /**
   * When true, only the body scrolls; header stays fixed (card uses flex column + overflow hidden).
   * Fixes close button scrolling away on tall dialogs (e.g. Edit Role).
   */
  bodyScrollOnly?: boolean;
  /**
   * `centeredTitle` — centered heading + absolute close (Angular `Edit Role` dialog).
   * `iconTitle` — left icon + title with close on the right (modern form dialogs).
   * Default — left title + close (standard OnScreenModal).
   */
  headerVariant?: "default" | "centeredTitle" | "iconTitle";
  /** Optional leading icon shown when `headerVariant` is `iconTitle`. */
  titleIcon?: ReactNode;
  /** Optional subtitle under the title when `headerVariant` is `iconTitle`. */
  subtitle?: ReactNode;
  /** Footer action alignment — default `center`. */
  footerAlign?: "center" | "end";
  /**
   * Fixed footer below the scrollable body (same shell as reimbursements / applicant modals).
   * When set, the panel uses a stacked flex layout so the footer stays visible.
   */
  footer?: ReactNode;
}

export function OnScreenModal({
  isOpen,
  title,
  children,
  onClose,
  hideClose = false,
  maxWidth = "max-w-lg",
  overlayDataTestId,
  panelDataTestId,
  closeButtonDataTestId,
  titleDataTestId,
  bodyClassName,
  overlayClassName,
  panelClassName,
  headerClassName,
  footerClassName,
  bodyScrollOnly = false,
  headerVariant = "default",
  titleIcon,
  subtitle,
  footerAlign = "center",
  footer,
}: OnScreenModalProps) {
  useBodyScrollLock(isOpen);

  if (!isOpen) return null;

  const widthClass = MAX_WIDTH_CLASS[maxWidth] ?? modalStyles.modalContent32;

  const stackedLayout = Boolean(footer) || bodyScrollOnly;
  const contentShellClass = `${modalStyles.modalContent} ${widthClass}${
    footer
      ? ` ${modalStyles.modalContentStacked}`
      : bodyScrollOnly
        ? ` ${modalStyles.modalContentBodyScroll}`
        : ""
  }${panelClassName ? ` ${panelClassName}` : ""}`;
  const headerChromeClass = modalStyles.modalShellHeaderChrome;
  const footerChromeClass = modalStyles.modalShellFooterChrome;

  const content = (
    <div
      className={`${modalStyles.modalOverlay} !z-[10001]${overlayClassName ? ` ${overlayClassName}` : ""}`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="onscreen-modal-title"
      data-testid={overlayDataTestId}
    >
      <div
        className={contentShellClass}
        onClick={(e) => e.stopPropagation()}
        data-testid={panelDataTestId}
      >
        {headerVariant === "iconTitle" ? (
          <div
            className={`${modalStyles.modalIconTitleHeader}${stackedLayout ? " shrink-0" : ""}${headerClassName ? ` ${headerClassName}` : ""}`}
          >
            <div className={modalStyles.modalIconTitleLead}>
              {titleIcon ? (
                <span className={modalStyles.modalIconTitleIcon} aria-hidden="true">
                  {titleIcon}
                </span>
              ) : null}
              <div className="min-w-0">
                <h3
                  id="onscreen-modal-title"
                  data-testid={titleDataTestId}
                  className={modalStyles.modalIconTitleText}
                >
                  {title}
                </h3>
                {subtitle ? (
                  <p
                    className="m-0 mt-0.5 text-sm font-normal text-slate-500"
                    data-testid={
                      titleDataTestId ? `${titleDataTestId}-subtitle` : undefined
                    }
                  >
                    {subtitle}
                  </p>
                ) : null}
              </div>
            </div>
            {!hideClose && (
              <button
                type="button"
                onClick={onClose}
                className={modalStyles.closeButton}
                aria-label="Close"
                data-testid={closeButtonDataTestId}
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        ) : (
          <div className={`${modalStyles.modalValidationHeader} ${headerChromeClass}${stackedLayout ? " shrink-0" : ""}${headerClassName ? ` ${headerClassName}` : ""}`}>
            <h3
              id="onscreen-modal-title"
              data-testid={titleDataTestId}
              className={modalStyles.modalValidationTitle}
            >
              {title}
            </h3>
            {!hideClose && (
              <button
                type="button"
                onClick={onClose}
                className={`${modalStyles.closeButton} ${modalStyles.modalValidationClose}`}
                aria-label="Close"
                data-testid={closeButtonDataTestId}
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        )}
        <div
          className={`${modalStyles.modalBodyConfirmTight} custom-scrollbar${
            stackedLayout
              ? " min-h-0 min-w-0 flex-1 overflow-y-auto break-words"
              : " min-w-0 max-h-[70vh] overflow-y-auto break-words"
          }${bodyClassName ? ` ${bodyClassName}` : ""}`}
        >
          {children}
        </div>
        {footer ? (
          <div
            className={`shrink-0 ${footerAlign === "end" ? modalStyles.modalShellFooterEnd : footerChromeClass} px-5 py-3 sm:px-6${footerClassName ? ` ${footerClassName}` : ""}`}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
